/* -----------------Globals--------------- */
import { useEffect, useState } from "react";

/* -----------------Child components--------------- */
import StudySessionView from "../components/StudySessionView";

import { useLocalSearchParams } from "expo-router";
/* -----------------Helpers & Hooks--------------- */
import useSessionStore from "../hooks/useSessionStore";

import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { replayManager } from "@helpers/lib/sentry";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { isDev } from "@memorang/constants/urls";
import Loader from "@memorang/ui/components/Loader";
import { useDevice } from "@memorang/ui/useDevice";
import { AudioPlaybackTestDialog } from "../components/AudioPlaybackTestDialog";
import PracticeSessionView from "../components/PracticeSessionView";
import MoreItemsAlertDialog from "../components/dialogs/MoreItemsAlertDialog";
import SessionDisclaimerDialog from "../components/dialogs/StudySessionDisclaimerDialog";
import useCurrentSession from "../hooks/useCurrentSession";
/* -----------------Types--------------- */
import { useResumeSession } from "../hooks/useResumeSession";

const SessionContainer = () => {
	const numItemsRequested = useSessionStore((state) => state.numItemsRequested);
	const sessionId = useSessionStore((state) => state.id);

	const showMoreItemsAlert = useSessionStore(
		(state) => state.showMoreItemsAlert,
	);
	const isSummativeTest = useSessionStore((state) => state.isSummativeTest);
	const totalTime = useSessionStore((state) => state.totalTime);
	const inReviewMode = useSessionStore((state) => state.inReviewMode);
	const sessionSubType = useSessionStore((state) => state.sessionSubType);

	const enableSessionReplay = useAppStore(
		(state) => state.sessionView?.enableSessionReplay,
	);

	const shouldEnableSessionReplay =
		enableSessionReplay && isSummativeTest && !inReviewMode && !isDev;

	useEffect(() => {
		if (shouldEnableSessionReplay) {
			replayManager().startReplay();
		} else {
			replayManager().stopReplay();
		}
	}, [shouldEnableSessionReplay]);

	const disclaimer = useAppStore((state) => state?.sessionView?.disclaimer);

	const [showDisclaimer, setShowDisclaimer] = useState(disclaimer != null);

	const handleCloseDisclaimer = () => {
		setShowDisclaimer(false);
	};

	const {
		currentBlock,
		currentBlockIndex,
		numSessionItems: numSections,
		numTotalItems,
	} = useCurrentSession();

	const { isMobile } = useDevice();

	const toggleShowMoreItemsAlert = useSessionStore(
		(state) => state.toggleShowMoreItemsAlert,
	);

	const deviceRecommendationDialog = useAppStore(
		(state) => state.exam?.practiceTest?.deviceRecommendationDialog,
	);
	const shouldShowDeviceRecommendationDialog =
		deviceRecommendationDialog?.enabled && isMobile;

	const [showDeviceRecommendationDialog, setShowDeviceRecommendationDialog] =
		useState(shouldShowDeviceRecommendationDialog);

	const handleCloseDeviceRecommendationDialog = () => {
		setShowDeviceRecommendationDialog(false);
	};

	const { handleResumeSession } = useResumeSession();
	const { viewerId } = useAuthContext();

	const showAudioTestDialog = useAppStore(
		(state) => state.app?.accountSettings?.showAudioTestDialog,
	);

	const showAudioPlaybackTestDialogGlobal = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const [showAudioPlaybackTestDialog, setShowAudioPlaybackTestDialog] =
		useState(showAudioPlaybackTestDialogGlobal && showAudioTestDialog);

	const id = useLocalSearchParams<{
		id: string;
	}>().id;

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!sessionId && viewerId && id) {
			handleResumeSession(Number(id));
		}
	}, [id, sessionId, viewerId]);

	if (!sessionId) {
		return <Loader />;
	}

	if (isSummativeTest) {
		return (
			<>
				<PracticeSessionView
					sessionItems={currentBlock}
					sessionId={sessionId}
					currentBlockIndex={currentBlockIndex}
					totalTime={totalTime}
					showDisclaimer={showDisclaimer || showDeviceRecommendationDialog}
					inReviewMode={inReviewMode}
					reportSessionType={sessionSubType}
					numSections={numSections}
					numTotalItems={numTotalItems}
				/>
				{showDisclaimer && disclaimer && !inReviewMode && (
					<SessionDisclaimerDialog
						visible={showDisclaimer}
						handleClose={handleCloseDisclaimer}
						text={disclaimer}
						title={"Disclaimer"}
					/>
				)}
				{showDeviceRecommendationDialog && (
					<SessionDisclaimerDialog
						visible={showDeviceRecommendationDialog}
						handleClose={handleCloseDeviceRecommendationDialog}
						text={deviceRecommendationDialog?.message}
						title={deviceRecommendationDialog?.title}
					/>
				)}
			</>
		);
	}

	return (
		<>
			<StudySessionView sessionItems={currentBlock} />
			{showMoreItemsAlert && (
				<MoreItemsAlertDialog
					visible={showMoreItemsAlert}
					handleClose={toggleShowMoreItemsAlert}
					numItemsAvailable={currentBlock.length}
					numItemsRequested={numItemsRequested}
				/>
			)}
			{showAudioPlaybackTestDialog && (
				<AudioPlaybackTestDialog
					showDialog={true}
					handleClose={() => {
						setShowAudioPlaybackTestDialog(false);
					}}
				/>
			)}
			{showDisclaimer && disclaimer && !inReviewMode && (
				<SessionDisclaimerDialog
					visible={showDisclaimer}
					handleClose={handleCloseDisclaimer}
					text={disclaimer}
					title={"Disclaimer"}
				/>
			)}
		</>
	);
};

export default SessionContainer;
